import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2,
  RefreshCw,
  History as HistoryIcon,
  ArrowRight,
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadixSelect } from "@/components/ui/select";
import { Pagination } from "@/components/elements/pagination";
import DateTimeDisplay from "@/components/elements/date-time-display";
import useGetNodeChangeLogsService from "@/service-hooks/use-get-node-change-logs-service";
import { NodeChangeLog, NodeChangeLogUser } from "@/types/history";

const PAGE_SIZE = 10;

const LABEL_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Persona", label: "Persona" },
  { value: "Outcome", label: "Outcome" },
  { value: "Scenario", label: "Scenario" },
  { value: "Step", label: "Step" },
  { value: "Action", label: "Action" },
  { value: "UserJourney", label: "User Journey" },
  { value: "Flow", label: "Flow" },
  { value: "Page", label: "Page" },
  { value: "Component", label: "Component" },
  { value: "UserExperience", label: "User Experience" },
  { value: "ApiGateway", label: "Api Gateway" },
  { value: "ObservabilityMonitoring", label: "Observability & Monitoring" },
  { value: "Agents", label: "Agents" },
  { value: "Services", label: "Services" },
  { value: "EventQueue", label: "Event Queue" },
  { value: "DataLake", label: "Data Lake" },
  { value: "Infrastructure", label: "Infrastructure" },
  { value: "DDLTable", label: "DDL Table", groupHeader: "Relational" },
  { value: "DDLColumn", label: "DDL Column" },
  { value: "DDLConstraint", label: "DDL Constraint" },
  { value: "DDLIndex", label: "DDL Index" },
  { value: "DDLView", label: "DDL View" },
  { value: "DDLProcedure", label: "DDL Procedure" },
  { value: "DDLSequence", label: "DDL Sequence" },
  { value: "ESIndex", label: "ES Index", groupHeader: "Elasticsearch" },
  { value: "ESField", label: "ES Field" },
  { value: "ESAlias", label: "ES Alias" },
  { value: "NoSQLCollection", label: "NoSQL Collection", groupHeader: "NoSQL" },
  { value: "NoSQLField", label: "NoSQL Field" },
  { value: "NoSQLIndex", label: "NoSQL Index" },
  { value: "GraphNodeType", label: "Graph Node Type", groupHeader: "Graph database" },
  { value: "GraphEdgeType", label: "Graph Edge Type" },
  { value: "GraphIndex", label: "Graph Index" },
];

function formatDateHeading(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function actionStyles(action: string): string {
  const a = action?.toLowerCase();
  if (a === "create") return "bg-green-500/20 text-green-700 border-green-500/40";
  if (a === "delete") return "bg-red-500/20 text-red-700 border-red-500/40";
  if (a === "rename") return "bg-purple-500/20 text-purple-700 border-purple-500/40";
  if (a === "update") return "bg-blue-500/20 text-blue-700 border-blue-500/40";
  return "bg-surface-hover text-text-primary border-surface-border";
}

function actionAccent(action: string): {
  iconRing: string;
  hoverBorder: string;
  glow: string;
} {
  const a = action?.toLowerCase();
  if (a === "create")
    return {
      iconRing: "ring-green-500/50",
      hoverBorder: "hover:border-green-500/40",
      glow: "group-hover:shadow-green-500/10",
    };
  if (a === "delete")
    return {
      iconRing: "ring-red-500/50",
      hoverBorder: "hover:border-red-500/40",
      glow: "group-hover:shadow-red-500/10",
    };
  if (a === "rename")
    return {
      iconRing: "ring-purple-500/50",
      hoverBorder: "hover:border-purple-500/40",
      glow: "group-hover:shadow-purple-500/10",
    };
  if (a === "update")
    return {
      iconRing: "ring-blue-500/50",
      hoverBorder: "hover:border-blue-500/40",
      glow: "group-hover:shadow-blue-500/10",
    };
  return {
    iconRing: "ring-surface-border",
    hoverBorder: "hover:border-surface-border",
    glow: "",
  };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function userDisplayName(
  primary?: NodeChangeLogUser,
  fallback?: NodeChangeLogUser
): string {
  const pick = primary?.firstName || primary?.lastName ? primary : fallback;
  if (!pick) return "Unknown user";
  const name = `${pick.firstName ?? ""} ${pick.lastName ?? ""}`.trim();
  return name || pick.email || "Unknown user";
}

function getNodeDisplayName(log: NodeChangeLog): string {
  const meta = log.nodeMetadata ?? {};
  const labelKey = (log.label || log.nodeLabel || "").toLowerCase();
  if (labelKey && typeof meta[labelKey] === "string" && meta[labelKey]) {
    return meta[labelKey] as string;
  }
  for (const key of ["name", "title", "label"]) {
    const v = (meta as Record<string, unknown>)[key];
    if (typeof v === "string" && v) return v;
  }
  return (
    (log.nodeName as string) ||
    (log.field as string) ||
    (log.nodeId as string) ||
    "Node"
  );
}

interface FieldDiff {
  field: string;
  previous: unknown;
  updated: unknown;
}

function computeMetadataDiff(
  current?: Record<string, unknown>,
  previous?: Record<string, unknown>
): FieldDiff[] {
  if (!current || !previous) return [];
  const diffs: FieldDiff[] = [];
  const keys = new Set([
    ...Object.keys(current),
    ...Object.keys(previous),
  ]);
  const ignore = new Set([
    "id",
    "projectUuid",
    "_sourceNodeId",
    "embeddingPlatform",
    "functionalId",
  ]);
  for (const key of keys) {
    if (ignore.has(key)) continue;
    const a = current[key];
    const b = previous[key];
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      diffs.push({ field: key, previous: b, updated: a });
    }
  }
  return diffs;
}

export default function NodeHistoryTabLayout() {
  const { id = "" } = useParams();
  const [selectedLabel, setSelectedLabel] = useState<string>("All");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const labelFilter = selectedLabel === "All" ? undefined : selectedLabel;

  const { data, isLoading, isError, refetch, isFetching } =
    useGetNodeChangeLogsService(
      id,
      !!id,
      labelFilter,
      currentPage,
      PAGE_SIZE,
      sortOrder
    );

  const logs = data?.data ?? [];
  const totalItems = data?.total ?? 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLabel, sortOrder]);

  const enrichedLogs = useMemo(() => {
    // For each log, find the previous (chronologically earlier) log for the
    // same nodeId so we can show a metadata diff on "update" actions.
    const byNode: Record<string, NodeChangeLog[]> = {};
    for (const log of logs) {
      const key = String(log.nodeId ?? "");
      if (!key) continue;
      if (!byNode[key]) byNode[key] = [];
      byNode[key].push(log);
    }
    for (const key of Object.keys(byNode)) {
      byNode[key].sort((a, b) => {
        const ta = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const tb = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return ta - tb;
      });
    }
    const previousMetaById = new Map<string | number, Record<string, unknown>>();
    for (const list of Object.values(byNode)) {
      for (let i = 0; i < list.length; i++) {
        const prev = i > 0 ? list[i - 1].nodeMetadata : undefined;
        if (prev) previousMetaById.set(list[i]._id, prev);
      }
    }
    return logs.map((log) => ({
      log,
      previousMetadata: previousMetaById.get(log._id),
    }));
  }, [logs]);

  // Render order follows the server's sortOrder; no client-side re-sort.
  const sortedLogs = enrichedLogs;

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const groupedLogs = useMemo(() => {
    const groups: Record<
      string,
      { log: NodeChangeLog; previousMetadata?: Record<string, unknown> }[]
    > = {};
    for (const entry of sortedLogs) {
      const ts = entry.log.createdAt || entry.log.updatedAt;
      if (!ts) continue;
      const dateKey = new Date(ts).toISOString().split("T")[0];
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(entry);
    }
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === b) return 0;
      return sortOrder === "asc" ? (a < b ? -1 : 1) : a < b ? 1 : -1;
    });
    return { groups, sortedKeys };
  }, [sortedLogs, sortOrder]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-text-primary mb-1">
            Node Change History
          </h2>
          <p className="text-sm text-text-secondary">
            Tracks create, update, delete, and rename actions performed on
            individual nodes in this semantic model.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-44">
            <RadixSelect
              options={LABEL_OPTIONS}
              value={selectedLabel}
              onChange={setSelectedLabel}
              placeholder="Filter by type"
            />
          </div>
          <Button
            onClick={() =>
              setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
            }
            variant="outline"
            size="icon"
            disabled={isFetching}
            title={
              sortOrder === "desc"
                ? "Newest first — click to switch to oldest first"
                : "Oldest first — click to switch to newest first"
            }
            className="border-surface-border text-white hover:bg-surface-hover hover:border-primary"
          >
            {sortOrder === "desc" ? (
              <ArrowDownNarrowWide className="h-4 w-4" />
            ) : (
              <ArrowUpNarrowWide className="h-4 w-4" />
            )}
          </Button>
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="icon"
            disabled={isFetching}
            className="border-surface-border text-white hover:bg-surface-hover hover:border-primary"
          >
            <RefreshCw
              className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-1">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="text-text-primary w-5 h-5 animate-spin" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3 text-center">
            <p className="text-sm font-medium text-text-primary">
              Unable to load node history
            </p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              className="border-surface-border text-white hover:bg-surface-hover hover:border-primary"
            >
              Retry
            </Button>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-surface-hover flex items-center justify-center">
              <HistoryIcon className="w-6 h-6 text-muted" />
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">
                {selectedLabel === "All"
                  ? "No node changes yet"
                  : `No ${
                      LABEL_OPTIONS.find((o) => o.value === selectedLabel)
                        ?.label ?? selectedLabel
                    } changes yet`}
              </p>
              <p className="text-xs text-muted mt-1">
                Changes to nodes in this semantic model will appear here.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pb-4">
            {groupedLogs.sortedKeys.map((dateKey) => (
              <div key={dateKey}>
                <div className="flex items-center gap-2 mb-2 pl-2 pr-1 pt-0.5">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/25 to-primary/10 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/30">
                    <Calendar className="w-3.5 h-3.5" />
                  </div>
                  <h3 className="text-sm font-semibold text-text-primary tracking-tight">
                    {formatDateHeading(dateKey)}
                  </h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-surface-border to-transparent" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted px-2 py-0.5 rounded-full bg-surface-hover border border-surface-border">
                    {groupedLogs.groups[dateKey].length}{" "}
                    {groupedLogs.groups[dateKey].length === 1
                      ? "change"
                      : "changes"}
                  </span>
                </div>
                <div className="space-y-3">
                  {groupedLogs.groups[dateKey].map(
                    ({ log, previousMetadata }) => {
                      const displayName = getNodeDisplayName(log);
                      const nodeLabel = log.label || log.nodeLabel;
                      const isUpdate = log.action?.toLowerCase() === "update";
                      const diffs = isUpdate
                        ? computeMetadataDiff(log.nodeMetadata, previousMetadata)
                        : [];
                      const showLegacyDiff =
                        !diffs.length &&
                        (log.previousValue !== undefined ||
                          log.updatedValue !== undefined);
                      const accent = actionAccent(log.action);
                      const userName = userDisplayName(log.approver, log.user);
                      return (
                        <div
                          key={String(log._id)}
                          className={`group relative rounded-xl p-4 bg-surface-card border border-surface-border shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${accent.hoverBorder} ${accent.glow} animate-in fade-in slide-in-from-bottom-2`}
                        >
                          <div className="flex gap-3.5">
                            <div
                              title={userName}
                              className={`shrink-0 w-10 h-10 rounded-full bg-surface-hover text-text-primary border border-surface-border flex items-center justify-center text-xs font-semibold transition-transform duration-200 group-hover:scale-105 ring-2 ${accent.iconRing}`}
                            >
                              {initials(userName)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`px-2 py-0.5 text-[10px] font-bold rounded-md border uppercase tracking-wider ${actionStyles(
                                    log.action
                                  )}`}
                                >
                                  {log.action}
                                </span>
                                {nodeLabel && (
                                  <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-surface-hover text-text-primary border border-surface-border">
                                    {nodeLabel}
                                  </span>
                                )}
                              </div>
                              <p className="text-base font-semibold text-text-primary mt-2 tracking-tight truncate">
                                {displayName}
                              </p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-muted">
                                <span className="text-text-secondary font-medium">
                                  {userName}
                                </span>
                                <span className="text-surface-border">•</span>
                                <DateTimeDisplay
                                  utcDate={log.createdAt || log.updatedAt || ""}
                                />
                              </div>
                              {diffs.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {diffs.map((d) => (
                                    <div
                                      key={d.field}
                                      className="rounded-lg border border-surface-border bg-surface-hover/60 p-3 backdrop-blur-sm"
                                    >
                                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary mb-2">
                                        {d.field}
                                      </p>
                                      <div className="relative grid grid-cols-1 md:grid-cols-2 md:items-stretch gap-2">
                                        <div className="rounded-md border border-red-500/25 bg-gradient-to-br from-red-500/10 to-red-500/5 p-2.5">
                                          <p className="text-[9px] font-bold uppercase tracking-wider text-red-600 mb-1">
                                            Previous
                                          </p>
                                          <p className="text-sm font-medium text-text-primary break-words whitespace-pre-wrap line-through decoration-red-500/50">
                                            {renderValue(d.previous)}
                                          </p>
                                        </div>
                                        <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center w-7 h-7 rounded-full bg-surface-card border border-surface-border shadow-md">
                                          <ArrowRight className="w-3.5 h-3.5 text-text-secondary" />
                                        </div>
                                        <div className="rounded-md border border-green-500/25 bg-gradient-to-br from-green-500/10 to-green-500/5 p-2.5">
                                          <p className="text-[9px] font-bold uppercase tracking-wider text-green-600 mb-1">
                                            Updated
                                          </p>
                                          <p className="text-sm font-semibold text-text-primary break-words whitespace-pre-wrap">
                                            {renderValue(d.updated)}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {showLegacyDiff && (
                                <div className="mt-3 relative grid grid-cols-1 md:grid-cols-2 md:items-stretch gap-2">
                                  <div className="rounded-md border border-red-500/25 bg-gradient-to-br from-red-500/10 to-red-500/5 p-2.5">
                                    <p className="text-[9px] font-bold uppercase tracking-wider text-red-600 mb-1">
                                      Previous
                                    </p>
                                    <p className="text-sm font-medium text-text-primary break-words whitespace-pre-wrap line-through decoration-red-500/50">
                                      {renderValue(log.previousValue)}
                                    </p>
                                  </div>
                                  <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center w-7 h-7 rounded-full bg-surface-card border border-surface-border shadow-md">
                                    <ArrowRight className="w-3.5 h-3.5 text-text-secondary" />
                                  </div>
                                  <div className="rounded-md border border-green-500/25 bg-gradient-to-br from-green-500/10 to-green-500/5 p-2.5">
                                    <p className="text-[9px] font-bold uppercase tracking-wider text-green-600 mb-1">
                                      Updated
                                    </p>
                                    <p className="text-sm font-semibold text-text-primary break-words whitespace-pre-wrap">
                                      {renderValue(log.updatedValue)}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isLoading && !isError && totalItems > 0 && (
        <div className="mt-3 pt-3 border-t border-surface-border shrink-0">
          <Pagination
            currentItems={sortedLogs.length}
            totalItems={totalItems}
            currentPage={safePage}
            totalPages={totalPages}
            isLoading={isFetching}
            onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            canGoPrevious={safePage > 1}
            canGoNext={safePage < totalPages}
            itemName="changes"
            showPageNumbers
            mode="pages"
          />
        </div>
      )}
    </div>
  );
}
